import { useCallback, useEffect, useRef } from 'react';

/**
 * Runs `action` now if `isReady`, otherwise queues the last args and runs
 * once when `isReady` becomes true.
 *
 * @param {boolean} isReady
 * @param {(...args: *) => void} action
 * @returns {(...args: *) => boolean} `true` if the call was queued.
 */
export const useDeferredReadyAction = (isReady, action) => {
    const pendingArgsRef = useRef(null);

    useEffect(() => {
        if (!isReady || pendingArgsRef.current === null) return;

        const args = pendingArgsRef.current;

        pendingArgsRef.current = null;
        action(...args);
    }, [isReady, action]);

    return useCallback(
        (...args) => {
            if (isReady) {
                action(...args);
                return false;
            }

            pendingArgsRef.current = args;
            return true;
        },
        [isReady, action]
    );
};
